import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, MultiSelectField } from './fields';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import {
  EdiCsrsOptions, getEdiCsrsOptions, getEdiCsrMembers, saveEdiCsrMembers, ediCsrsHistoryEndpoint,
  EdiLcRule, EdiRuleOptions, getEdiCsrRules, getEdiRuleOptions, saveEdiCsrRules,
} from '../../services/edicsrs.service';

/**
 * Bespoke edit/create drawer for EDI LCs (cms.edi_csrs) — a many-to-many LC ↔ EDI
 * handle presented GROUPED by handle. The drawer edits the LC set for one handle:
 * pick the EDI Handle (dropdown, V1 @edihandle_data) + one or more LCs (shared LC
 * roster). Choosing a handle loads its current LC set (so an already-mapped handle
 * merges rather than wipes), then Save reconciles the set via /edicsrs/members
 * (insert/reactivate added, soft-delete removed). Requires a handle + ≥1 LC. On edit
 * the handle is fixed (it's the group key). Replaces V1 tablemaint.pl editedi_csrs.
 */
const TABLE = 'edi-csrs';
const s = (v: any): string => (v == null ? '' : String(v));

// --- lane rules -------------------------------------------------------------
// A rule row held as multiselect arrays; joined to the API's comma lists on save.
type RuleDraft = {
  recno: number;
  billtos: string[];
  customers: string[];
  movetypes: string[];
  deliveries: string[];
};
const AXES = ['billtos', 'customers', 'movetypes', 'deliveries'] as const;
const emptyRule = (): RuleDraft => ({ recno: 0, billtos: [], customers: [], movetypes: [], deliveries: [] });
const splitList = (v: string): string[] => (v || '').split(',').map((x) => x.trim()).filter(Boolean);
const toDraft = (r: EdiLcRule): RuleDraft => ({
  recno: r.recno,
  billtos: splitList(r.billtolist),
  customers: splitList(r.customerlist),
  movetypes: splitList(r.movetypelist),
  deliveries: splitList(r.deliverylist),
});
const toApi = (d: RuleDraft): EdiLcRule => ({
  recno: d.recno,
  billtolist: d.billtos.join(','),
  customerlist: d.customers.join(','),
  movetypelist: d.movetypes.join(','),
  deliverylist: d.deliveries.join(','),
});
/** Tier = number of CONSTRAINED axes. Mirrors the server; shown so "why does Bob get
 *  this and not Amy" is answerable without reading the query. */
const tierOf = (d: RuleDraft): number => AXES.filter((a) => d[a].length > 0).length;
const asOptions = (opts: { value: number | string; label: string }[] | undefined) =>
  (opts ?? []).map((o) => ({ value: String(o.value), label: o.label }));

const LockedField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-muted mb-1">{label}</label>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {children}
    </div>
  </div>
);

const EdiCsrsEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;

  const [opts, setOpts] = useState<EdiCsrsOptions | null>(null);
  const [edihandle, setEdihandle] = useState(s(row?.edihandle));
  const [csrs, setCsrs] = useState<string[]>([]);
  // Whether the chosen handle already had active LCs when loaded — drives the save
  // button label (a merge into an existing handle reads as "Save Changes").
  const [hadExisting, setHadExisting] = useState(!isNew);
  const [membersLoading, setMembersLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  // Lane rules for this handle, keyed by LC. An LC absent from here (or with an empty
  // array) works the ENTIRE handle and is its fallback owner.
  const [rulesByLc, setRulesByLc] = useState<Record<string, RuleDraft[]>>({});
  const [ruleOpts, setRuleOpts] = useState<EdiRuleOptions | null>(null);
  const [rulesLoading, setRulesLoading] = useState(false);

  useEffect(() => { getEdiCsrsOptions().then(setOpts).catch(() => {}); }, []);

  // Load the chosen handle's current LC set (both modes) so editing operates on the
  // full set — picking an already-mapped handle merges instead of wiping its LCs.
  useEffect(() => {
    const h = edihandle.trim();
    if (!h) { setCsrs([]); setHadExisting(false); return; }
    let alive = true;
    setMembersLoading(true);
    getEdiCsrMembers(h)
      .then((m) => { if (alive) { setCsrs(m); setHadExisting(m.length > 0); } })
      .catch(() => { if (alive) { setCsrs([]); setHadExisting(false); } })
      .finally(() => { if (alive) setMembersLoading(false); });
    return () => { alive = false; };
  }, [edihandle]);

  // Load the handle's lane rules + its axis choices alongside the members. Both are
  // per-handle, so they reload together whenever the handle changes.
  useEffect(() => {
    const h = edihandle.trim();
    if (!h) { setRulesByLc({}); setRuleOpts(null); return; }
    let alive = true;
    setRulesLoading(true);
    Promise.all([getEdiCsrRules(h), getEdiRuleOptions(h)])
      .then(([res, axes]) => {
        if (!alive) return;
        const map: Record<string, RuleDraft[]> = {};
        for (const g of res.lcs) map[g.csr] = g.rules.map(toDraft);
        setRulesByLc(map);
        setRuleOpts(axes);
      })
      .catch(() => { if (alive) { setRulesByLc({}); setRuleOpts(null); } })
      .finally(() => { if (alive) setRulesLoading(false); });
    return () => { alive = false; };
  }, [edihandle]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const setRule = useCallback((csr: string, idx: number, patch: Partial<RuleDraft>) => {
    setRulesByLc((prev) => {
      const next = [...(prev[csr] || [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, [csr]: next };
    });
  }, []);
  const addRule = useCallback((csr: string) => {
    setRulesByLc((prev) => ({ ...prev, [csr]: [...(prev[csr] || []), emptyRule()] }));
  }, []);
  const removeRule = useCallback((csr: string, idx: number) => {
    setRulesByLc((prev) => ({ ...prev, [csr]: (prev[csr] || []).filter((_, i) => i !== idx) }));
  }, []);

  // Only rules for LCs still in the membership list are kept — dropping an LC from
  // "LC List" must take their rules with them, or the save would be rejected for a
  // rule whose csr is no longer a member.
  const liveRules = useMemo(
    () => csrs.map((csr) => ({ csr, rules: (rulesByLc[csr] || []) })),
    [csrs, rulesByLc],
  );
  const scopedCount = liveRules.filter((g) => g.rules.length > 0).length;
  // Some LCs lane-scoped and others not: the un-scoped ones are now catch-all-only.
  const mixed = scopedCount > 0 && scopedCount < csrs.length;

  // Add mode: hide handles that already have an active mapping (edit those via the
  // grid) so we don't create a second group for the same handle.
  const mappedHandles = useMemo(() => new Set(opts?.mapped_handles ?? []), [opts]);
  const addHandleOpts = useMemo(
    () => (opts?.edihandles ?? []).filter((o) => !mappedHandles.has(o.value)),
    [opts, mappedHandles],
  );
  const csrOpts = useMemo(() => opts?.csrs ?? [], [opts]);
  const saveLabel = !isNew || hadExisting ? 'Save Changes' : 'Create Mapping';

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!edihandle.trim()) e.push('EDI Handle is required.');
    if (csrs.length === 0) e.push('At least one LC is required.');
    // Mirrors the server's rejection so the user is told before the round trip. An
    // all-blank rule is tier 0 — it matches everything, so it wins only when nothing
    // narrower does, which is already what having no rule means.
    for (const { csr, rules } of liveRules) {
      if (rules.some((r) => tierOf(r) === 0)) {
        e.push(`${csr} has an empty lane rule. Choose at least one Customer, Delivery Location, `
             + `Move Type or Bill To — or remove the rule to make ${csr} a fallback owner for the whole handle.`);
      }
    }
    return e;
  }, [edihandle, csrs, liveRules]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      // Membership FIRST, then rules: the rules endpoint rejects a rule for an LC with
      // no active membership, so a newly-added LC has to exist before their rules land.
      await saveEdiCsrMembers(edihandle.trim(), csrs);
      await saveEdiCsrRules(edihandle.trim(), liveRules.map((g) => ({ csr: g.csr, rules: g.rules.map(toApi) })));
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save EDI LC mapping.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, edihandle, csrs, liveRules, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {saveLabel}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add EDI LC Mapping' : `Edit EDI LC Mapping — ${s(row?.edihandle)}`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="46rem" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {edihandle.trim() && (
          <div className="flex justify-end">
            <EditHistoryLink endpoint={ediCsrsHistoryEndpoint(edihandle.trim())} label={edihandle.trim()} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isNew ? (
            <SelectField label="EDI Handle" required value={edihandle}
              onChange={setEdihandle} options={addHandleOpts} disabled={isSaving} />
          ) : (
            <LockedField label="EDI Handle">{s(row?.edihandle) || <span className="text-gray-400">—</span>}</LockedField>
          )}
          {!isNew && (
            <LockedField label="Bill To">
              {row?.billtoname ? titleCase(s(row.billtoname)) : <span className="text-gray-400">—</span>}
            </LockedField>
          )}
        </div>

        <MultiSelectField
          label="LC List"
          required
          disabled={isSaving || membersLoading}
          loading={!opts || membersLoading}
          value={csrs}
          onChange={setCsrs}
          options={csrOpts}
          placeholder={edihandle.trim() ? 'Add LCs…' : 'Choose an EDI Handle first…'}
        />

        {/* --- Lane rules ---------------------------------------------------
            Optional narrowing per LC. Leaving an LC with no rules is a real and
            common choice: they work the entire handle and pick up whatever no
            rule claims, which is also how you nominate a fallback owner. */}
        {edihandle.trim() && csrs.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <label className="block text-sm font-medium text-muted">Lane Rules</label>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Most specific rule wins · equal rules share
              </span>
            </div>

            {mixed && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 p-3 text-sm text-amber-900 dark:text-amber-200">
                <span className="font-medium">
                  {scopedCount} of {csrs.length} LCs on this handle have lane rules.
                </span>{' '}
                The other {csrs.length - scopedCount} now only receive orders that no rule
                matches. That is a valid setup — it makes them the fallback — but if you
                meant everyone to keep working the whole handle, give them rules too or
                remove the rules above.
              </div>
            )}

            {rulesLoading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading lane rules…</div>
            ) : (
              <div className="space-y-2">
                {liveRules.map(({ csr, rules }) => (
                  <div key={csr} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-body">{csr}</span>
                      {rules.length === 0 ? (
                        <span
                          className="text-xs rounded px-1.5 py-px text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-700"
                          title="No rules — this LC works every order on the handle that no other LC's rule claims"
                        >
                          Entire handle
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {rules.length} rule{rules.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>

                    {rules.map((r, idx) => (
                      <div key={idx} className="mt-3 rounded-md bg-gray-50 dark:bg-gray-800/60 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {tierOf(r) === 0
                              ? 'No axes chosen — this rule is incomplete'
                              : `Matches on ${tierOf(r)} of 4 · beats any rule matching on fewer`}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeRule(csr, idx)}
                            disabled={isSaving}
                            className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <MultiSelectField
                            label="Move Type" disabled={isSaving} value={r.movetypes}
                            onChange={(v: string[]) => setRule(csr, idx, { movetypes: v })}
                            options={asOptions(ruleOpts?.movetypes)} placeholder="Any" />
                          <MultiSelectField
                            label="Delivery Location" disabled={isSaving} value={r.deliveries}
                            onChange={(v: string[]) => setRule(csr, idx, { deliveries: v })}
                            options={asOptions(ruleOpts?.deliveries)} placeholder="Any" />
                          <MultiSelectField
                            label="Customer" disabled={isSaving} value={r.customers}
                            onChange={(v: string[]) => setRule(csr, idx, { customers: v })}
                            options={asOptions(ruleOpts?.customers)} placeholder="Any" />
                          <MultiSelectField
                            label="Bill To" disabled={isSaving} value={r.billtos}
                            onChange={(v: string[]) => setRule(csr, idx, { billtos: v })}
                            options={asOptions(ruleOpts?.billtos)} placeholder="Any" />
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => addRule(csr)}
                      disabled={isSaving}
                      className="mt-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                    >
                      + Add lane rule
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default EdiCsrsEditDrawer;
